/**
 * Client-side executors for the template chat's "document tools".
 *
 * The template-mode chat agent pulls document context on demand: instead of
 * being force-fed the whole document every turn, it calls a tool — which the
 * backend surfaces as a LangGraph interrupt. `GaiaChatView` runs the matching
 * executor here against the live Word document (Office.js) and resumes the
 * agent with the result. This keeps each turn's context to the minimum the
 * task needs and scales to documents far larger than the per-turn cap.
 *
 * Office add-in only: the underlying Word helpers throw on other hosts, so the
 * caller resumes the agent with `{ ok: false, error }` and the model copes.
 */
import {
  applyDocumentEdits,
  exportCurrentDocumentAsDocx,
  readDocumentBodyText,
  readParagraphsWithStyle,
  searchDocumentRanges,
  type DocumentEdit,
  type DocumentParagraph,
} from '@/office/word/templateWord';
import { TemplateAuthoringService } from '@/services/contractGeneration/templateAuthoringService';

const CHUNK_DEFAULT_SIZE = 8000;
const CHUNK_MIN_SIZE = 1000;
const CHUNK_MAX_SIZE = 20000;
const SEARCH_DEFAULT_MAX = 20;
const SEARCH_HARD_MAX = 50;

export const DOCUMENT_TOOL_NAMES = [
  'search',
  'get_outline',
  'get_section',
  'get_chunks',
  'apply_edits',
  'validate',
  'list_fields',
] as const;
export type DocumentToolName = (typeof DOCUMENT_TOOL_NAMES)[number];

function clampInt(value: unknown, fallback: number, min: number, max: number): number {
  const parsed = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.min(max, Math.max(min, Math.trunc(parsed)));
}

/**
 * Style-based heading level (1–9), else null. A minimal replica of the
 * heading detection in `playbookWord.ts` — style names only, no prose
 * heuristic (an outline tool only needs real Word headings; a doc with none
 * yields an empty outline and the model falls back to search/chunks).
 */
function headingLevel(paragraph: DocumentParagraph): number | null {
  for (const style of [paragraph.styleBuiltIn, paragraph.style]) {
    const match = style?.trim().match(/^heading\s*(\d)$/i);
    if (match) {
      const level = Number.parseInt(match[1], 10);
      if (level >= 1 && level <= 9) return level;
    }
  }
  return null;
}

interface OutlineHeading {
  level: number;
  title: string;
  /** Paragraph index — also the `ordinal` the model passes back to get_section. */
  ordinal: number;
}

function buildOutline(paragraphs: DocumentParagraph[]): OutlineHeading[] {
  const headings: OutlineHeading[] = [];
  for (const [ordinal, paragraph] of paragraphs.entries()) {
    const level = headingLevel(paragraph);
    const title = paragraph.text.trim();
    if (level && title) headings.push({ level, title, ordinal });
  }
  return headings;
}

async function runSearch(args: Record<string, unknown>): Promise<unknown> {
  const query = typeof args.query === 'string' ? args.query : '';
  const maxResults = clampInt(args.max_results, SEARCH_DEFAULT_MAX, 1, SEARCH_HARD_MAX);
  const matches = await searchDocumentRanges(query, maxResults);
  return { matches };
}

async function runOutline(): Promise<unknown> {
  const paragraphs = await readParagraphsWithStyle();
  return { headings: buildOutline(paragraphs) };
}

async function runSection(args: Record<string, unknown>): Promise<unknown> {
  const paragraphs = await readParagraphsWithStyle();
  const outline = buildOutline(paragraphs);
  if (outline.length === 0) {
    return { title: null, text: '', note: 'No headings found in the document.' };
  }

  const wantedTitle = typeof args.heading === 'string' ? args.heading.trim().toLowerCase() : null;
  const wantedOrdinal = typeof args.ordinal === 'number' ? args.ordinal : null;

  let target: OutlineHeading | undefined;
  if (wantedOrdinal !== null) {
    target = outline.find(heading => heading.ordinal === wantedOrdinal);
  } else if (wantedTitle) {
    target =
      outline.find(heading => heading.title.toLowerCase() === wantedTitle) ??
      outline.find(heading => heading.title.toLowerCase().includes(wantedTitle));
  }

  if (!target) {
    return {
      title: null,
      text: '',
      note: 'Heading not found.',
      headings: outline.map(heading => heading.title),
    };
  }

  // From the heading paragraph up to the next heading of the same-or-higher level.
  const start = target.ordinal;
  let end = paragraphs.length;
  for (let i = start + 1; i < paragraphs.length; i += 1) {
    const level = headingLevel(paragraphs[i]);
    if (level && level <= target.level) {
      end = i;
      break;
    }
  }
  const text = paragraphs
    .slice(start, end)
    .map(paragraph => paragraph.text)
    .join('\n')
    .trim();
  return { title: target.title, text };
}

async function runChunks(args: Record<string, unknown>): Promise<unknown> {
  const body = await readDocumentBodyText();
  const size = clampInt(args.size, CHUNK_DEFAULT_SIZE, CHUNK_MIN_SIZE, CHUNK_MAX_SIZE);
  const total = Math.max(1, Math.ceil(body.length / size));
  const index = clampInt(args.index, 0, 0, total - 1);
  return { index, total, text: body.slice(index * size, (index + 1) * size) };
}

function parseEdit(raw: unknown): DocumentEdit | null {
  if (!raw || typeof raw !== 'object') return null;
  const record = raw as Record<string, unknown>;
  const find = typeof record.find === 'string' ? record.find : null;
  const replace = typeof record.replace === 'string' ? record.replace : null;
  if (find === null || replace === null) return null;
  const occurrence =
    record.occurrence === 'all' || typeof record.occurrence === 'number'
      ? record.occurrence
      : undefined;
  return { find, replace, occurrence };
}

async function runApplyEdits(args: Record<string, unknown>): Promise<unknown> {
  const rawEdits = Array.isArray(args.edits) ? args.edits : [];
  const edits = rawEdits.map(parseEdit).filter((edit): edit is DocumentEdit => edit !== null);
  if (edits.length === 0) return { applied: 0, total: 0, failures: [] };
  return applyDocumentEdits(edits);
}

/**
 * Validate the live document — the same check as the Editor's "Test current
 * document" button: export the open docx and run it through the backend
 * extractor. Lets the agent verify its work after a conversion/edit and fix any
 * problems. `valid` reflects only error-severity issues (warnings don't block).
 */
async function runValidate(): Promise<unknown> {
  const docx = await exportCurrentDocumentAsDocx('document.docx');
  const result = await TemplateAuthoringService.validateUpload(docx);
  const validationErrors = result.validation_errors ?? [];
  const blocking = validationErrors.filter(error => (error.severity ?? 'error') === 'error');
  return {
    valid: blocking.length === 0,
    validation_errors: validationErrors,
    field_count: Object.keys(result.fields ?? {}).length,
  };
}

/**
 * List the fields already defined in the live document — the top-level
 * placeholder names + inferred types from the same extraction `validate` uses.
 * The template assistant calls this first for naming consistency (so it reuses
 * `natural_person_seller` etc. instead of inventing new aliases). Top level only
 * — nested entity sub-fields are intentionally omitted to keep it compact.
 */
async function runListFields(): Promise<unknown> {
  const docx = await exportCurrentDocumentAsDocx('document.docx');
  const result = await TemplateAuthoringService.validateUpload(docx);
  const fields = Object.entries(result.fields ?? {}).map(([name, field]) => ({
    name,
    type: field.type ?? null,
  }));
  return { fields };
}

/**
 * Run a document tool by name and return a JSON-serializable result. Throws on
 * an unknown tool or when there's no live document (non-Office host) — the
 * caller turns that into `{ ok: false, error }` on resume.
 */
export async function executeDocumentTool(
  tool: string,
  args: Record<string, unknown>
): Promise<unknown> {
  switch (tool) {
    case 'search':
      return runSearch(args);
    case 'get_outline':
      return runOutline();
    case 'get_section':
      return runSection(args);
    case 'get_chunks':
      return runChunks(args);
    case 'apply_edits':
      return runApplyEdits(args);
    case 'validate':
      return runValidate();
    case 'list_fields':
      return runListFields();
    default:
      throw new Error(`Unknown document tool: ${tool}`);
  }
}
